"use client";

import type { LeadFormExitMethod } from "@homeinstead/analytics";

import Phone from "@homeinstead/icons/Phone";
import { cn } from "@homeinstead/ui";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
} from "@homeinstead/ui/components/Dialog";
import Logo from "@homeinstead/ui/components/Logo";
import Link from "next/link";
import { useCallback, useMemo, useRef } from "react";
import { TrackedPhoneLink } from "~www/components/TrackedPhoneLink";
import { usePrefersReducedMotion } from "~www/hooks/use-prefers-reduced-motion";
import { LeadFormScrollTargetContext } from "./lead-form-scroll-target-context";
import { useTrackLeadFormEvent } from "./useTrackLeadFormEvent";

// The takeover's open animation is fixed, not CMS-authored — Sanity's
// fullscreenTakeover object only ever authors `enabled`. The panel appears
// instantly; only the chrome and step content stage in on top of it (the
// ENTRANCE_* classes below).
const FULLSCREEN_TAKEOVER_ANIMATION = "fade";
const FULLSCREEN_TAKEOVER_ANIMATION_DURATION_MS = 0;

// Each element needs its own `fade-in-0`/`slide-in-from-bottom-3` (they
// don't cascade from `animate-in`) plus `fill-mode-both`, holding it at its
// start state until its delay elapses.
//
// `ease-out` and `delay-75`/`150`/`300` are real steps on both Tailwind
// core's and tailwindcss-animate's scales, so they stay plain classes.
// `400` isn't a step on either scale: `duration-400` generates no CSS, and
// `duration-[400ms]` is ambiguous between the two plugins' `duration`
// utility and also drops — hence the `[animation-duration:400ms]` escape
// below.
const TAKEOVER_ENTRANCE_BASE_CLASSES =
  "animate-in fade-in-0 slide-in-from-bottom-3 ease-out fill-mode-both [animation-duration:400ms]";
// Logo, then actions, then content — content trails by only one delay step
// since it's what the user clicked Continue to reach, not incidental chrome.
const TAKEOVER_ENTRANCE_LOGO_CLASSES = cn(
  TAKEOVER_ENTRANCE_BASE_CLASSES,
  "delay-75",
);
const TAKEOVER_ENTRANCE_ACTIONS_CLASSES = cn(
  TAKEOVER_ENTRANCE_BASE_CLASSES,
  "delay-150",
);
const TAKEOVER_ENTRANCE_CONTENT_CLASSES = cn(
  TAKEOVER_ENTRANCE_BASE_CLASSES,
  "delay-300",
);

type LeadFormTakeoverDialogProps = {
  /** The active step's analytics name (step.metricsLabel ?? step.id) — fires "Lead Form Exit" on dismiss when present. */
  analyticsStepName?: string;
  children: React.ReactNode;
  /**
   * The lead form's own `<form>` element, passed as the Dialog's portal
   * container so Radix renders the panel inside it instead of
   * `document.body` — a body portal would detach the submit button.
   */
  container: HTMLElement | null;
  /**
   * Unwinds browser history back to step 1 (use-lead-form-takeover.ts's
   * onCloseTakeover) — the dialog closes as a consequence, not through
   * Radix's own open state.
   */
  onClose: () => void;
  /** Route's resolved office phone; omits the Call Us CTA when absent. */
  phoneNumber?: string;
  /** The active step's name, used as the dialog's accessible name. */
  title: string;
};

/**
 * Fullscreen takeover chrome for the LFB. `open` has no `onOpenChange`:
 * Escape and the close button both dismiss via `handleDismiss`, which fires
 * "Lead Form Exit" analytics before calling `onClose`, which navigates back
 * to step 1 instead of toggling Radix's own open state.
 */
function LeadFormTakeoverDialog({
  analyticsStepName,
  children,
  container,
  onClose,
  phoneNumber,
  title,
}: LeadFormTakeoverDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const trackLeadForm = useTrackLeadFormEvent();

  const handleDismiss = useCallback(
    (exitMethod: LeadFormExitMethod) => {
      if (analyticsStepName) {
        trackLeadForm?.leadFormExit({
          exitMethod,
          stepName: analyticsStepName,
        });
      }
      onClose();
    },
    [analyticsStepName, onClose, trackLeadForm],
  );

  // DialogBody is the takeover's only scrollport — the window behind a
  // fixed-height panel can't scroll. Read inside the callback, not mirrored
  // into state, so scrollTarget's identity stays stable.
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollTarget = useMemo(
    () => ({
      scrollStepRegionToTop: () => {
        bodyRef.current?.scrollTo({ behavior: "smooth", top: 0 });
      },
    }),
    [],
  );

  return (
    <Dialog open>
      <DialogContent
        // Reduced motion appears instantly, the same way useStepTransition
        // advances synchronously under the same query.
        animation={
          prefersReducedMotion ? "none" : FULLSCREEN_TAKEOVER_ANIMATION
        }
        animationDurationMs={FULLSCREEN_TAKEOVER_ANIMATION_DURATION_MS}
        aria-describedby={undefined}
        aria-label={title}
        className="bg-clear-sky-tints-500"
        container={container}
        fullscreen
        // FocusScope would otherwise land on DialogCloseButton (first
        // tabbable node), but it's inert until dismissal ships. Focus the
        // panel so role="dialog" + aria-label announce the step name.
        // LeadForm/ThankYou claim focus on close; skip Radix's own delayed
        // restore so it doesn't clobber that with <body>.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={() => handleDismiss("escape")}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          panelRef.current?.focus({ preventScroll: true });
        }}
        // slide-up's off-screen start would otherwise create scrollable
        // overflow inside the overlay's own overflow-y-auto and flash a
        // scrollbar mid-animation; the takeover's own scrolling region is
        // DialogBody, not the overlay.
        overlayClassName="overflow-hidden"
        ref={panelRef}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 p-6">
          <Link
            aria-label="Visit homeinstead corporate"
            className={cn(
              !prefersReducedMotion && TAKEOVER_ENTRANCE_LOGO_CLASSES,
            )}
            href="/"
          >
            <Logo className="h-10 w-[123px] sm:h-12 sm:w-[148px]" />
          </Link>
          <div
            className={cn(
              "flex items-center gap-2 sm:gap-3",
              !prefersReducedMotion && TAKEOVER_ENTRANCE_ACTIONS_CLASSES,
            )}
          >
            {phoneNumber ? (
              <TrackedPhoneLink
                // h-11 matches DialogCloseButton's size-11.
                className="h-11 w-auto shrink-0 gap-2 px-4"
                phoneNumber={phoneNumber}
                placement="lead form"
                variant="primary"
              >
                <Phone className="fill-current" />
                <span className="md:hidden">Call us</span>
                <span className="hidden md:inline">{phoneNumber}</span>
              </TrackedPhoneLink>
            ) : null}
            {/*
             * Ghost + a smaller glyph than the default: a discoverable exit
             * without inviting users to leave mid-funnel.
             */}
            <DialogCloseButton
              className="static inset-auto"
              iconClassName="size-4"
              onClick={() => handleDismiss("x_button")}
              variant="ghost"
            />
          </div>
        </div>
        <DialogBody className="px-0 pb-0 sm:pb-6" ref={bodyRef}>
          {/*
           * Block wrapper, not flex-col: flex-col would shrink
           * LeadFormSubmissionError (see lead-form-builder.md § fullscreen
           * takeover stacking). Bottom padding lives here, not on
           * DialogBody, since padding on the scrollport itself doesn't leave
           * visible space after overflowing content. `max()` clears the iOS
           * home indicator.
           */}
          <div
            className={cn(
              "mx-auto w-full max-w-screen-hd px-4 pb-[max(2.5rem,env(safe-area-inset-bottom))] sm:px-10 sm:pb-0 lg:px-[72px] 2xl:px-[152px]",
              !prefersReducedMotion && TAKEOVER_ENTRANCE_CONTENT_CLASSES,
            )}
          >
            {/*
             * Retargets the scroll-on-advance seam from the window, which
             * can't scroll behind a fixed 100dvh panel, to this scrollport.
             */}
            <LeadFormScrollTargetContext value={scrollTarget}>
              {children}
            </LeadFormScrollTargetContext>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export { LeadFormTakeoverDialog };
